// Landing page data configuration
// This file makes it easy to update content without modifying components

import React from 'react';
import QualityAssuranceIcon from '../icons/QualityAssuranceIcon';
import LiveAgentAssistanceIcon from '../icons/LiveAgentAssistanceIcon';
import VoiceAgentIcon from '../icons/VoiceAgentIcon';
import MomentIntelligenceIcon from '../icons/MomentIntelligenceIcon';
import FlowAwarenessIcon from '../icons/FlowAwarenessIcon';
import TrendSensitivityIcon from '../icons/TrendSenstivity';

// Image display patterns for feature cards
export type ImagePattern = 'single' | 'two-horizontal' | 'two-vertical' | 'three-stacked';

export interface FeatureImage {
  src: string;
  alt: string;
}

export interface FeatureImageConfig {
  pattern: ImagePattern;
  images: FeatureImage[];
}

export type ProductCta =
  | { type: 'route'; label: string; path: string }
  | { type: 'anchor'; label: string; href: string };

export interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  highlights: string[];
  icon: React.ReactElement;
  accent: string;
  primaryCta: ProductCta;
  secondaryCta: ProductCta;
}

export const platformInfo = {
  name: 'Vhearu',
  hero: 'Talk to an AI agent that actually gets work done',
  heroHighlight: 'actually gets work done',
  subtext:
    'Live voice agents that follow your workflows — plus AutoQA and AgentAssist so every conversation elevates performance.',
  industryHint: 'Banking · Real estate · HVAC',
  platformArc: {
    label: 'Listen → Coach → Act',
    description:
      'Evaluate every conversation, coach agents in the moment, and deploy voice agents that take action.',
  },
  pillars: [
    {
      title: 'Listen',
      description: 'AutoQA scores quality, compliance, and moments across every interaction.',
      icon: React.createElement(MomentIntelligenceIcon, { size: 48, className: 'arc-icon-svg' }),
    },
    {
      title: 'Coach',
      description: 'AgentAssist guides live agents with real-time prompts and targeted feedback.',
      icon: React.createElement(FlowAwarenessIcon, { size: 48, className: 'arc-icon-svg' }),
    },
    {
      title: 'Act',
      description: 'Voice Agent speaks with customers, follows workflows, and completes tasks.',
      icon: React.createElement(TrendSensitivityIcon, { size: 48, className: 'arc-icon-svg' }),
    },
  ],
};

export const products: Product[] = [
  {
    id: 'product-autoqa',
    name: 'AutoQA',
    tagline: 'Evaluate every conversation',
    description:
      'Automated quality assurance that scores every customer interaction across quality, compliance, and business outcomes.',
    highlights: [
      'Real-time call evaluation',
      'Multi-dimensional scoring',
      'Compliance & script checks',
      'Performance trend analysis',
    ],
    icon: React.createElement(QualityAssuranceIcon, { size: 72, className: 'product-icon-svg' }),
    accent: '#0d9488',
    primaryCta: { type: 'anchor', label: 'See capabilities', href: '#features' },
    secondaryCta: { type: 'anchor', label: 'Learn more', href: '#product-autoqa' },
  },
  {
    id: 'product-assist',
    name: 'AgentAssist',
    tagline: 'Coach agents in the moment',
    description:
      'Live guidance and personalized coaching so human agents stay on script, close gaps, and improve with every call.',
    highlights: [
      'Real-time coaching prompts',
      'Personalized recommendations',
      'Performance gap analysis',
      'Peer benchmarking',
    ],
    icon: React.createElement(LiveAgentAssistanceIcon, { size: 72, className: 'product-icon-svg' }),
    accent: '#0284c7',
    primaryCta: { type: 'anchor', label: 'See capabilities', href: '#features' },
    secondaryCta: { type: 'anchor', label: 'Learn more', href: '#product-assist' },
  },
  {
    id: 'product-voice',
    name: 'Voice Agent',
    tagline: 'Act on behalf of your brand',
    description:
      'Customer-facing AI that speaks naturally, follows approved workflows, and completes tasks — try a live demo with no signup.',
    highlights: [
      'Natural speech in & out',
      'Workflow & policy adherence',
      'Multi-industry live demos',
      'Actions during the call',
    ],
    icon: React.createElement(VoiceAgentIcon, { size: 72, className: 'product-icon-svg' }),
    accent: '#ea580c',
    primaryCta: { type: 'route', label: 'Try live demo', path: 'live-demo' },
    secondaryCta: { type: 'anchor', label: 'Learn more', href: '#product-voice' },
  },
];

export const features = [
  {
    name: 'Voice Agent',
    description:
      'Deploy customer-facing voice AI that follows your workflows, speaks naturally, and takes action — try live demos across banking, real estate, and HVAC.',
    highlights: [
      'Workflow adherence in real time',
      'Natural speech in and out',
      'Industry-ready live demos',
      'No signup to try',
    ],
    icon: React.createElement(VoiceAgentIcon, { size: 28, className: 'feature-icon-svg' }),
    color: '#ea580c',
    cta: { type: 'route' as const, label: 'Try live demo', path: 'live-demo' },
    imageConfig: undefined as FeatureImageConfig | undefined,
  },
  {
    name: 'Key Moments',
    description:
      'Moment Intelligence deciphers agent-customer interactions automatically to identify behaviors and patterns critical to business success.',
    highlights: [
      'Automatic behavior detection',
      'Pattern recognition',
      'Critical moment identification',
      'Business impact analysis',
    ],
    icon: React.createElement(MomentIntelligenceIcon, { size: 28, className: 'feature-icon-svg' }),
    color: '#0d9488',
    cta: { type: 'anchor' as const, label: 'Explore AutoQA', href: '#product-autoqa' },
    imageConfig: {
      pattern: 'three-stacked' as ImagePattern,
      images: [
        {
          src: '/feature-images/interactive-timeline.png',
          alt: 'Key Moments feature showing behavior detection and pattern recognition',
        },
        {
          src: '/feature-images/chat-with-tags.png',
          alt: 'Key Moments feature showing behavior detection and pattern recognition',
        },
        {
          src: '/feature-images/transcript-mmts.png',
          alt: 'Key Moments feature showing behavior detection and pattern recognition',
        },
      ],
    },
  },
  {
    name: 'Compliance & Script Adherence',
    description:
      'Ensure every call meets regulatory standards while keeping agents aligned with approved scripts and workflows.',
    highlights: [
      'Regulatory compliance monitoring',
      'Script adherence tracking',
      'Workflow alignment',
      'Standards enforcement',
    ],
    icon: React.createElement(QualityAssuranceIcon, { size: 28, className: 'feature-icon-svg' }),
    color: '#0284c7',
    cta: { type: 'anchor' as const, label: 'Explore AutoQA', href: '#product-autoqa' },
    imageConfig: {
      pattern: 'two-vertical' as ImagePattern,
      images: [
        {
          src: '/feature-images/script-adherence-kpis.png',
          alt: 'Compliance monitoring dashboard',
        },
        {
          src: '/feature-images/script-adherence.png',
          alt: 'Script adherence tracking interface',
        },
      ],
    },
  },
  {
    name: 'Interactive Timeline',
    description: 'Navigate calls with AI-curated highlights for faster, smarter insights.',
    highlights: ['AI-curated highlights', 'Smart navigation', 'Faster insights', 'Interactive exploration'],
    icon: React.createElement(FlowAwarenessIcon, { size: 28, className: 'feature-icon-svg' }),
    color: '#0f766e',
    cta: { type: 'anchor' as const, label: 'Explore AutoQA', href: '#product-autoqa' },
    imageConfig: {
      pattern: 'two-vertical' as ImagePattern,
      images: [
        {
          src: '/feature-images/interactive-timeline-transcript.png',
          alt: 'Interactive timeline with AI highlights',
        },
        {
          src: '/feature-images/interactive-timeline-kpis.png',
          alt: 'Interactive timeline with AI highlights',
        },
      ],
    },
  },
  {
    name: 'Auto Evaluation',
    description:
      'Automatically evaluates every call in real time, scoring quality, compliance, and performance.',
    highlights: [
      'Real-time evaluation',
      'Multi-dimensional scoring',
      'Quality assessment',
      'Performance metrics',
    ],
    icon: React.createElement(QualityAssuranceIcon, { size: 28, className: 'feature-icon-svg' }),
    color: '#147a6e',
    cta: { type: 'anchor' as const, label: 'Explore AutoQA', href: '#product-autoqa' },
    imageConfig: {
      pattern: 'two-horizontal' as ImagePattern,
      images: [
        {
          src: '/feature-images/evaluation-sectionscores.png',
          alt: 'Chat interface with evaluation tags',
        },
        {
          src: '/feature-images/evaluation-questionscores.png',
          alt: 'Chat interface with evaluation tags',
        },
      ],
    },
  },
  {
    name: 'KPIs & Dashboards',
    description:
      'Extract measurable insights from conversations and organize them into dashboards for visibility into agent, team, and organizational performance.',
    highlights: [
      'Measurable insights extraction',
      'Performance dashboards',
      'Trend analysis',
      'Growth opportunity identification',
    ],
    icon: React.createElement(TrendSensitivityIcon, { size: 28, className: 'feature-icon-svg' }),
    color: '#c2410c',
    cta: { type: 'anchor' as const, label: 'Explore AutoQA', href: '#product-autoqa' },
    imageConfig: {
      pattern: 'single' as ImagePattern,
      images: [
        {
          src: '/feature-images/kpis-dashboards.png',
          alt: 'KPIs and performance dashboards',
        },
      ],
    },
  },
  {
    name: 'Targeted Coaching',
    description:
      'AI recommends personalized coaching based on performance gaps and relative benchmarking against peers.',
    highlights: [
      'Personalized recommendations',
      'Performance gap analysis',
      'Peer benchmarking',
      'Improvement maximization',
    ],
    icon: React.createElement(LiveAgentAssistanceIcon, { size: 28, className: 'feature-icon-svg' }),
    color: '#0369a1',
    cta: { type: 'anchor' as const, label: 'Explore AgentAssist', href: '#product-assist' },
    imageConfig: {
      pattern: 'single' as ImagePattern,
      images: [
        {
          src: '/feature-images/feedback.png',
          alt: 'Targeted coaching recommendations',
        },
      ],
    },
  },
];
